import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField, Typography, useTheme } from "@mui/material";
import { MprcButton, MprcFormSubHeader, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { ButtonBack } from "components/buttons";
import { IconButtonClose } from "components/iconButtons";
import { MprcResolveRequestErrors } from "components/MprcRequestErrorReport";
import { MprcSwitch } from "components/MprcSwitch";
import { ManagedQuestionEvaluationDto, ManagedQuestionResponse, ManagedQuestionSurveyDto, QuestionOpenAIDto, QuestionType } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MAX_IA_ALTERNATIVES } from "views/assessment/common/assessment";
import { GeneratorQuestionAIList } from "views/assessment/generatorQuestion/GeneratorQuestionAIList";
import { GeneratorQuestionAIOption } from "views/assessment/generatorQuestion/GeneratorQuestionAIOption";
import { IPostValidation, IValidationFields, QuestionContext, QuestionEditMode, getEmptyQuestionDto } from "views/assessment/questionBank/Home/EditQuestion/EditQuestion";
import { validateAlternatives } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionAlternative";
import { validadeAvailableQuestion } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionAvailableQuestion";
import { validateEssay } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionEssay";
import { validateFillIn } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionFillIn";
import { validadeMainBox } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionMainbox";
import { validateStar } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionStar";
import { EditQuestionPointOthers, validatePointOther } from "views/assessment/questionBank/Home/EditQuestion/Evaluation/EditQuestionPointOthers";
import { EditQuestionClassification } from "views/assessment/questionBank/Home/EditQuestion/Survey/EditQuestionClassification";

/////////////////////////////////////////////////////////////////////////////////

export interface GeneratorOptions {
    numberOfQuestions: number;
    numberOfAlternatives: number;
    subject: string;
    createFeedback: boolean;
    recommentPub: boolean;
}

interface IPros {
    questionGroupId?: string | null;
    exclusiveAssessmentId?: string;
    forSurvey: boolean;
    editMode: QuestionEditMode;
    hasPoints?: boolean;
    onClose: (success: boolean) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const GeneratorQuestionAI: React.FC<IPros> = (props) => {
    const { questionGroupId, exclusiveAssessmentId, forSurvey, editMode, hasPoints = true, onClose } = props;

    const api = useCoreApi();
    const theme = useTheme();
    const { t } = useTranslation("assessment");
    const { enqueueSnackbar } = useSnackbar();

    const [step, setStep] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [posting, setPosting] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [questions, setQuestions] = useState<QuestionOpenAIDto[]>([]);
    const [viewData, setViewData] = useState<ManagedQuestionResponse>(getEmptyQuestionDto(QuestionType.SimpleChoice, false, questionGroupId, exclusiveAssessmentId));
    const [viewFieldsErrors, setViewFieldsErrors] = useState<ApiProblemErrors>();
    const [continueCreating, setContinueCreating] = useState<boolean>(false);
    const [chatResponse, setChatResponse] = useState<string>();
    const success = useRef<boolean>(false);
    const [option, setOption] = useState<GeneratorOptions>({
        numberOfQuestions: 5,
        numberOfAlternatives: 5,
        subject: "",
        createFeedback: !forSurvey,
        recommentPub: false
    });

    const steps = [];
    const StepFirstPage = 1;
    const StepSecondPage = 2;
    const StepThirdPage = 3;
    const StepFourthPage = 4;

    ///
    function handleUpdateOption(name: keyof GeneratorOptions, v: any) {
        setOption({ ...option, [name]: v });
    }

    ///
    function handleUpdateRecord(name: keyof ManagedQuestionResponse, v: any) {
        setViewFieldsErrors(LibUtils.deleteObjectProp(viewFieldsErrors, name));
        setViewData({ ...viewData, [name]: v });
    }

    ///
    function handleUpdateEvaluation(name: keyof ManagedQuestionEvaluationDto, v: any) {
        setViewFieldsErrors(LibUtils.deleteObjectProp(viewFieldsErrors, name));
        setViewData({ ...viewData, evaluation: { ...viewData.evaluation!, [name]: v } });
    }

    ///
    function handleUpdateSurvey(name: keyof ManagedQuestionSurveyDto, v: any) {
        setViewFieldsErrors(LibUtils.deleteObjectProp(viewFieldsErrors, name));
        setViewData({ ...viewData, survey: { ...viewData.survey!, [name]: v } });
    }

    ///
    async function getOpenAIQuestions() {
        setLoading(true);
        try {
            var result = await api.v1.questionBank.getOpenAiQuestions({
                numberOfQuestions: option.numberOfQuestions,
                subject: option.subject,
                maxAlternatives: option.numberOfAlternatives,
                createFeedback: option.createFeedback,
                forSurvey
            });
            setQuestions(result.data.questions);
            setChatResponse(result.data.messageContent);
            setStep(StepThirdPage);
        }
        catch (e: any) {
            setViewError(e);
        }
        finally {
            setLoading(false);
        }
    }

    ///
    async function saveAIQuestions() {
        setPosting(true);
        try {
            var saveQuestions = questions.filter(x => x.selected).map(x => ({ ...x, alternatives: x.alternatives.filter(x => x.alternativeText.length > 0 && !x.isInfo) }));

            var result = await api.v1.questionBank.saveAiQuestion({
                questions: saveQuestions,
                evaluation: forSurvey ? null : viewData.evaluation,
                survey: forSurvey ? {
                    changeVersion: false,
                    essayAnswerRequired: false,
                    fillInMaxValue: null,
                    fillInMinValue: null,
                    pointTyped: false,
                    showSideBySide: false,
                } : null,
                tags: viewData.tags.map(x => x.label),
                availableQuestioBank: viewData.availableQuestioBank,
                exclusiveAssessmentId: exclusiveAssessmentId ?? null,
                questionGroupId: viewData.questionGroupId ?? null,
                isBlocked: viewData.isBlocked,
                publicationRecommendedId: null
            });

            if (result.data) {
                success.current = true;
                enqueueSnackbar(t("GeneratorQuestionAI.SuccessMessage"), { variant: "success" });
            }

            if (continueCreating)
                setStep(StepFirstPage);
            else
                onClose(true);
        }
        catch (e: any) {
            setViewError(e);
        }
        finally {
            setPosting(false);
        }
    }

    ///
    const resolveErrors = (e: any | unknown) => {
        var r = MprcResolveRequestErrors(e);
        setViewFieldsErrors(r.fieldsWithError);
        setViewError(r.otherErrors);
        if (r.fieldsWithError != null) enqueueSnackbar(t("Record.SaveFailedWithFieldErrorsMessage", { ns: "shared" }), { variant: "error" });
    };

    // Validação de erros ao executar post.
    const vErrors = viewFieldsErrors ?? {};
    const postValidation: IPostValidation = {
        mainBox: {
            questionText: vErrors["questionText"],
        },
        alternativeBox: {
            alternatives: vErrors["alternatives"],
        },
        starBox: {
            alternatives: vErrors["alternatives"],
        },
        essayBox: {
            maxLen: vErrors["maxLen"],
            alternatives: vErrors["alternatives"],
        },
        fillInBox: {
            alternatives: vErrors["alternatives"],
            fillInMinValue: vErrors["fillInMinValue"],
            fillInMaxValue: vErrors["fillInMaxValue"],
        },
        feedbackBox: {
            trainingRecommended: vErrors["trainingRecommended"],
            knowledgePublicationRecommended: vErrors["knowledgePublicationRecommended"],
        },
        pointOtherBox: {
            points: vErrors["points"],
            isBlocked: vErrors["isBlocked"],
            allowInKnowledgeTests: vErrors["allowInKnowledgeTests"],
        },
        available: {
            questionGroupId: vErrors["questionGroupId"],
        }
    };

    // Validação dos campos de cada aba.
    const validationFields: IValidationFields = {
        mainBox: validadeMainBox(viewData),
        alternativeBox: validateAlternatives(viewData, false),
        starBox: validateStar(viewData),
        essayBox: validateEssay(viewData),
        fillInBox: validateFillIn(viewData, false),
        pointOtherBox: validatePointOther(viewData),
        available: validadeAvailableQuestion(viewData),
    };

    const canSave = validatePointOther(viewData).points;

    // Ao iniciar pela primeira vez...
    useEffect(() => {
        async function getDefaultValues() {
            // Definir valores default
            if (viewData.evaluation != null) {

                setLoading(true);

                try {
                    const result = await api.v1.questionBank.managedQuestionDefaultValues({ questionGroupId: props.questionGroupId ?? null });

                    if (result.data) {
                        setViewData({
                            ...viewData,
                            evaluation: {
                                ...viewData.evaluation,
                                allowInKnowledgeTests: result.data.allowInKnowledgeTests,
                                points: result.data.points
                            }
                        });
                    }
                }
                catch (e) {
                    setViewError(e);
                }
                finally {
                    setLoading(false);
                }
            }
        }

        getDefaultValues();

    }, []);

    ///
    steps[StepFirstPage] = () => {
        return (
            <>
                <DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: "400px" }}>
                    <GeneratorQuestionAIOption
                        header={
                            <Typography variant="h5" component="p" color={theme.palette.ai.main}>
                                {t("GeneratorQuestionAI.FirstPage.SubjectText")}
                            </Typography>
                        }
                        option={option}
                        onChange={(value) => setOption(value)}
                        forSurvey={forSurvey}
                        hasRecommend={false}
                    />
                </DialogContent>

                <DialogActions>
                    <MprcButton
                        variant="contained"
                        color="primary"
                        disabled={!(option.numberOfQuestions >= 1 && option.numberOfQuestions <= 20) || !(option.numberOfAlternatives >= 1 && option.numberOfAlternatives <= MAX_IA_ALTERNATIVES)}
                        text={t("GeneratorQuestionAI.FirstPage.ContinueButtonText")}
                        title={t("GeneratorQuestionAI.FirstPage.ContinueButtonTitle")}
                        onClick={() => setStep(StepSecondPage)}
                    />
                </DialogActions>
            </>
        );
    };

    ///
    steps[StepSecondPage] = () => {
        return (
            <>
                <DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: "400px" }}>
                    <MprcFormSubHeader
                        title={t("GeneratorQuestionAI.Subject.TitleText")}
                        subtitle={[
                            t("GeneratorQuestionAI.Subject.Subtitle_L1"),
                            t("GeneratorQuestionAI.Subject.Subtitle_L2"),
                        ]}
                    />
                    <Box sx={{
                        borderColor: "muted.main",
                        borderWidth: "1px",
                        borderStyle: "solid",
                        padding: "8px",
                        minHeight: "320px"
                    }}>
                        <TextField
                            name="subject"
                            fullWidth
                            autoComplete="off"
                            placeholder={t("GeneratorQuestionAI.Subject.SubjectPlaceholder")}
                            margin="dense"
                            multiline
                            autoFocus
                            value={option.subject}
                            slotProps={{ input: { disableUnderline: true } }}
                            onChange={(e) => handleUpdateOption("subject", e.target.value)}
                        />
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Grid container alignItems="center" spacing={1} width={"100%"}>
                        <Grid size="grow">
                            <MprcButton
                                variant="contained"
                                color="warning"
                                text={t("GeneratorQuestionAI.Subject.FillInButtonText")}
                                title={t("GeneratorQuestionAI.Subject.FillInButtonTitle")}
                                onClick={() => setOption({ ...option, subject: t(forSurvey ? "GeneratorQuestionAI.SampleSurveyText" : "GeneratorQuestionAI.SampleEvaluationText") })}
                            />
                        </Grid>
                        <Grid>
                            <ButtonBack
                                startIcon={null}
                                onClick={() => setStep(StepFirstPage)}
                            />
                        </Grid>
                        <Grid>
                            <MprcButton
                                variant="contained"
                                color="primary"
                                text={t("GeneratorQuestionAI.CreateButtonText")}
                                title={t("GeneratorQuestionAI.CreateButtonTitle")}
                                disabled={option.subject.length < 2}
                                onClick={() => getOpenAIQuestions()}
                            />
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };

    ///
    steps[StepThirdPage] = () => {
        return (
            <>
                <DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: "400px" }}>
                    {
                        questions.length == 0
                            ?
                            <>
                                <Typography variant="h3" color={theme.palette.error.main}>{t("GeneratorQuestionAI.ThirdPage.ErrorTitle")}</Typography>
                                <Typography variant="caption">{t("GeneratorQuestionAI.ThirdPage.ErrorHint")}</Typography>
                                <Box sx={{
                                    borderColor: "muted.main",
                                    borderWidth: "1px",
                                    borderStyle: "solid",
                                    padding: "8px",
                                    minHeight: "320px"
                                }}>
                                    <TextField
                                        name="subject"
                                        fullWidth
                                        autoComplete="off"
                                        margin="dense"
                                        multiline
                                        autoFocus
                                        value={chatResponse}
                                        slotProps={{ input: { disableUnderline: true } }}
                                        disabled
                                        maxRows={14}
                                    />
                                </Box>
                            </>
                            :
                            <>
                                <MprcFormSubHeader
                                    title={t("GeneratorQuestionAI.ThirdPage.Title")}
                                    subtitle={[
                                        t("GeneratorQuestionAI.ThirdPage.Subtitle_L1"),
                                        t("GeneratorQuestionAI.ThirdPage.Subtitle_L2"),
                                    ]}
                                />

                                <GeneratorQuestionAIList
                                    questions={questions}
                                    onChange={(value) => setQuestions(value)}
                                />
                            </>
                    }
                </DialogContent>

                <DialogActions sx={{ justifyContent: "space-between" }}>
                    <Typography variant="caption">
                        {t("shared:Search.IaDisclaimerMessage_L1")}<br />
                        {t("shared:Search.IaDisclaimerMessage_L2")}
                    </Typography>
                    <Stack direction="row" gap={1}>
                        <ButtonBack
                            startIcon={null}
                            onClick={() => setStep(StepSecondPage)}
                        />
                        <MprcButton
                            variant="contained"
                            color="primary"
                            text={t(!hasPoints ? "GeneratorQuestionAI.ThirdPage.SetOptionsButtonText" : "GeneratorQuestionAI.ThirdPage.SetPointsButtonText")}
                            title={t(!hasPoints ? "GeneratorQuestionAI.ThirdPage.SetOptionsButtonTitle" : "GeneratorQuestionAI.ThirdPage.SetPointsButtonTitle")}
                            disabled={questions.length == 0 || questions.filter(x => x.selected).length == 0}
                            onClick={() => setStep(StepFourthPage)}
                        />
                    </Stack>
                </DialogActions>
            </>
        );
    };

    ///
    steps[StepFourthPage] = () => {
        return (
            <>
                <DialogContent sx={{ display: "flex", flexDirection: "column", minHeight: "400px" }}>
                    {
                        forSurvey
                            ? <EditQuestionClassification
                                onError={error => resolveErrors(error)}
                            />
                            :
                            <EditQuestionPointOthers
                                onError={error => resolveErrors(error)}
                            />
                    }
                </DialogContent>

                <DialogActions>
                    <Grid container alignItems="center" spacing={1} width="100%">
                        <Grid size="grow">
                            <MprcSwitch
                                label={t("GeneratorQuestionAI.FouthPage.ContinueCreatingLabel")}
                                checked={continueCreating}
                                onChange={(e) => setContinueCreating(e.target.checked)}
                            />
                        </Grid>
                        <Grid>
                            <ButtonBack
                                startIcon={null}
                                onClick={() => setStep(StepThirdPage)}
                            />
                        </Grid>
                        <Grid>
                            <MprcButton
                                variant="contained"
                                color="primary"
                                disabled={!canSave}
                                text={t("GeneratorQuestionAI.FouthPage.SaveButtonText")}
                                title={t("GeneratorQuestionAI.FouthPage.SaveButtonTitle")}
                                onClick={() => saveAIQuestions()}
                            />
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            onClose={() => onClose(success.current)}
        >
            <MprcRequestErrorReport error={viewError} />
            <MprcRequestIndicator type={loading ? "request" : "post"} visible={loading || posting} />

            <DialogTitle>
                <Grid container justifyContent="space-between">
                    <Grid>
                        <Typography variant="h4" component="h1" color="muted.main">
                            {t("GeneratorQuestionAI.Title")}
                        </Typography>
                    </Grid>
                    <Grid>
                        <IconButtonClose
                            color="inherit"
                            size="small"
                            onClick={() => onClose(success.current)}
                        />
                    </Grid>
                </Grid>
            </DialogTitle>

            <QuestionContext.Provider
                value={{
                    viewData,
                    validationFields,
                    postValidation,
                    handleUpdateRecord,
                    handleUpdateEvaluation,
                    handleUpdateSurvey,
                    forSurvey,
                    readonly: false,
                    editMode
                }}
            >

                {steps[step]()}

            </QuestionContext.Provider>
        </Dialog>
    );
};
